import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Container, Typography, CircularProgress, Alert, Box, Link, Button, IconButton, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useBreadcrumb } from '../components/BreadcrumbContext';
import { apiFetch } from '../apiFetch';
import { Store } from '../types/Store';
import StoreFormModal from '../modals/StoreFormModal';
import DeleteModal from '../modals/DeleteModal';

interface LocationState {
  fromPrices?: {
    groceryId: number;
    groceryName: string;
  };
}

const ViewStore: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [store, setStore] = useState<Store | null>(null);

  // Get prices origin from location state
  const locationState = location.state as LocationState | null;
  const fromPrices = locationState?.fromPrices;
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { setOverrideLabel } = useBreadcrumb();

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchStore = async () => {
      try {
        const data = await apiFetch(`${apiUrl}/api/shops/${id}/`);
        setStore(data);
        setOverrideLabel(data.name);
      } catch (err: any) {
        setError(err.message || 'An error occurred while fetching store details.');
      } finally {
        setLoading(false);
      }
    };
    fetchStore();
  }, [apiUrl, id, setOverrideLabel]);

  const handleEditSuccess = (updatedStore: Store) => {
    setStore(updatedStore);
    setOverrideLabel(updatedStore.name);
  };

  const handleDeleteConfirm = async () => {
    if (!store) return;
    try {
      await apiFetch(`${apiUrl}/api/shops/${store.id}/`, {
        method: 'DELETE',
      });
      navigate('/stores', { state: { message: 'Store deleted successfully.' } });
    } catch (err: any) {
      setError(err.message || 'An error occurred while deleting the store.');
      setDeleteModalOpen(false);
    }
  };

  if (loading) {
    return <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />;
  }
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  if (!store) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Alert severity="warning">Store not found.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row">
          <IconButton onClick={() => navigate('/stores')} aria-label="back to stores">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">{store.name}</Typography>
          {store.store_status && (
            <Chip
              label={store.store_status.status}
              color={
                store.store_status.status === 'Open' ? 'success' :
                store.store_status.status === 'Closing Soon' ? 'warning' : 'default'
              }
              size="small"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          {fromPrices && (
            <Button
              component={RouterLink}
              to={`/prices/grocery/${fromPrices.groceryId}`}
              endIcon={<ArrowForwardIcon />}
              size="small"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {fromPrices.groceryName} Prices
            </Button>
          )}
          <Box className="page-header-buttons">
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => setFormModalOpen(true)}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Store Image */}
      <Box
        component="img"
        src={store.image_url || `${process.env.REACT_APP_IMAGES_URL}/placeholder-store.svg`}
        alt={store.name}
        sx={{ width: '100%', maxHeight: 300, objectFit: 'contain', mb: 1 }}
      />
      {store.image_attribution && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Photo: {store.image_attribution.replace(/<[^>]*>/g, '')}
        </Typography>
      )}

      {/* Address */}
      <Typography variant="body1" gutterBottom>
        <strong>Address:</strong> {store.address_line1}
        {store.address_line2 && `, ${store.address_line2}`}
        {store.city && `, ${store.city}`}
        {store.state && `, ${store.state}`}
        {store.postal_code && `, ${store.postal_code}`}
        {store.country && `, ${store.country}`}
      </Typography>

      {/* Phone */}
      {store.phone_number && (
        <Typography variant="body1" gutterBottom>
          <strong>Phone:</strong> {store.phone_number}
        </Typography>
      )}

      {/* Website */}
      {store.website && (
        <Typography variant="body1" gutterBottom>
          <strong>Website: </strong>
          <Link href={store.website} target="_blank" rel="noopener noreferrer" underline="hover">
            {store.website}
          </Link>
        </Typography>
      )}

      {/* Opening Hours */}
      {store.regular_hours && store.regular_hours.length > 0 && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            <strong>Opening Hours:</strong>
          </Typography>
          {store.regular_hours.map((hours) => (
            <Typography key={hours.id} variant="body2" color="text.secondary">
              {hours.day_name}: {hours.is_closed ? 'Closed' : `${hours.open_time} - ${hours.close_time}`}
            </Typography>
          ))}
        </Box>
      )}

      {/* Location */}
      {(store.latitude && store.longitude) && (
        <>
          <Typography variant="body1" gutterBottom>
            <strong>Location:</strong>{' '}
            <Link
              href={`https://www.google.com/maps?q=${store.latitude},${store.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              {store.latitude}, {store.longitude}
            </Link>
          </Typography>
          <Box
            sx={{
              width: '100%',
              height: 300,
              borderRadius: 1,
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              mt: 1,
              mb: 2,
            }}
          >
            <iframe
              title="Store Location"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${store.latitude},${store.longitude}&zoom=15`}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Map data provided by Google
          </Typography>
        </>
      )}

      {/* Edit Store Modal */}
      <StoreFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        onSuccess={handleEditSuccess}
        store={store}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={handleDeleteConfirm}
        itemName={store.name}
      />
    </Container>
  );
};

export default ViewStore;